import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Navigate,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  ArrowUpDown,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CreditCard,
  Heart,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  MoreHorizontal,
  Plus,
  Receipt,
  Search,
  Trash2,
} from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GeneratePaymentLinkDialog } from "@/components/features/accountant/GeneratePaymentLinkDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { matchesSearch } from "@/pages/accountant/carteraSearch";
import {
  describeTenantAction,
  type TenantActionKey,
} from "@/pages/accountant/tenantAction";
import {
  describeTenantRegime,
  type TenantRegimeKind,
  REGIME_LABEL as SHARED_REGIME_LABEL,
} from "@/lib/tenantRegime";
import {
  deleteOnboardingTenant,
  getMyAccountantProfile,
  getMyAccountantPulse,
  getMyTenantsSummary,
  type AccountantTenantFlag,
  type AccountantTenantSeverity,
  type TenantSummary,
} from "@/services/authService";

type FilterId =
  | "all"
  | "attention"
  | "onboarding"
  | "boletos"
  | "sin-dgi"
  | "activo"
  | "pago-pendiente"
  | "baja";
type SortId = "atencion" | "nombre" | "actividad";
type ViewId = "cards" | "list";

const FILTERS: Array<{ id: FilterId; label: string; urgent?: boolean }> = [
  { id: "all", label: "Todos" },
  { id: "attention", label: "Necesitan atención", urgent: true },
  { id: "onboarding", label: "Onboarding pendiente" },
  { id: "boletos", label: "Boletos sin pagar", urgent: true },
  // Subscription past_due or lapsed. Always shown even at zero, matching
  // "Boletos sin pagar" rather than the "Dadas de baja" edge-state chip: this
  // is a live billing alert, and a confirmed zero is worth seeing.
  { id: "pago-pendiente", label: "Pago fallido", urgent: true },
  { id: "sin-dgi", label: "Sin DGI" },
  { id: "activo", label: "Al día" },
  // Offboarded tenants — rendered only when there are any (see chip render).
  { id: "baja", label: "Dadas de baja" },
];

/**
 * Copy for each reason. Kept beside the card rather than inside
 * tenantAction.ts so the pure module stays free of i18n, and reusing the same
 * keys the per-flag pills used means none of this wording regressed.
 */
const ACTION_LABEL: Record<
  TenantActionKey,
  (t: TFunction, tenant: TenantSummary, count: number) => string
> = {
  onboarding: (t) =>
    t("accountant.card.onboardingPending", "Onboarding pendiente"),
  "sin-dgi": (t) => t("accountant.card.missingDgi", "DGI sin configurar"),
  rejected: (t, _tenant, count) =>
    t("accountant.card.rejected", {
      count,
      defaultValue: "{{count}} rechazo(s)",
    }),
  "pago-pendiente": (t, tenant) =>
    tenant.subscriptionState === "lapsed"
      ? t("accountant.card.subscriptionLapsed", "Suscripción vencida")
      : t("accountant.card.subscriptionPastDue", "Pago pendiente"),
  boletos: (t, _tenant, count) =>
    t("accountant.card.boletosUnpaid", {
      count,
      defaultValue: "{{count}} boleto(s) sin pagar",
    }),
  ack: (t, _tenant, count) =>
    t("accountant.card.pendingAck", {
      count,
      defaultValue: "{{count}} CFE por aceptar",
    }),
  vencimiento: (t) =>
    t("accountant.card.vencimiento", "Vencimiento esta semana"),
  iva: (t) => t("accountant.card.ivaClosing", "IVA cierra esta semana"),
  "beneficio-fiscal": (t) =>
    t("accountant.card.fiscalBenefitPending", "Falta declaración 206/019"),
};

/** Fiscal regime copy. The regime is what the client owes under, so it is
 *  shown for every tenant including the general case — a blank cell would
 *  read as missing data rather than as "nothing special". */
const REGIME_LABEL: Record<TenantRegimeKind, [string, string]> = {
  ...SHARED_REGIME_LABEL,
  // Not a regime — the fiscal profile has not been derived yet. Shown muted
  // and as a dash so it reads as a gap rather than a classification. This
  // rendering is LOCAL on purpose: the client-detail header drops the badge
  // entirely and settings says "no proporcionado", so the shared map omits
  // `unknown` rather than forcing one of the three on the others.
  unknown: ["accountant.regime.unknown", "—"],
};

/** Order shown in the régimen filter. "unknown" is last because it is a gap
 *  in the data, not a regime — but it stays selectable, since "which of my
 *  clients has no fiscal profile yet" is a real question for an accountant. */
const REGIME_FILTER_OPTIONS: TenantRegimeKind[] = [
  "literal-e",
  "cede",
  "no-cede",
  "servicios-personales",
  "monotributo",
  "unknown",
];

/**
 * brandColor is a free-text field in tenant settings, and this value is written
 * into a CSS custom property that a stylesheet then interpolates. Anything that
 * is not a colour — `url(...)` most obviously — would reach the CSS engine and
 * could fetch from a tenant-controlled host on any accountant's screen. Only a
 * plain hex colour is allowed through.
 */
function safeBrandColor(value: string | null): boolean {
  return value != null && /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim());
}

const ROLE_FALLBACKS: Record<string, string> = {
  accountant: "Contador",
  owner: "Propietario",
  admin: "Administrador",
  member: "Miembro",
  read_only: "Solo lectura",
};

function matchesFilter(tenant: TenantSummary, filter: FilterId): boolean {
  // Baja'd tenants live only under their own "Dadas de baja" chip — every
  // other filter (including "Todos") excludes them so they don't pollute the
  // active cartera or its counts.
  if (filter === "baja") return tenant.bajaAt != null;
  if (tenant.bajaAt != null) return false;
  if (filter === "all") return true;
  if (filter === "attention") return tenant.severity !== "ok";
  return (tenant.flags as AccountantTenantFlag[]).includes(filter);
}

const SEVERITY_RANK: Record<AccountantTenantSeverity, number> = {
  urgent: 0,
  attention: 1,
  ok: 2,
};

const SORT_STORAGE_KEY = "fiscalo:accountant-sort";
const VIEW_STORAGE_KEY = "fiscalo:accountant-view";
const PULSE_STORAGE_KEY = "fiscalo:accountant-pulse-expanded";

function readPersistedSort(): SortId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw === "atencion" || raw === "nombre" || raw === "actividad") {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

function persistSort(next: SortId): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SORT_STORAGE_KEY, next);
  } catch {
    // Storage unavailable — preference won't survive this session.
  }
}

function readPersistedView(): ViewId {
  if (typeof window === "undefined") return "cards";
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "cards";
  } catch {
    return "cards";
  }
}

// Pulse hero is collapsed by default — accountants live in the cards
// grid + filter chips, not the dark KPI summary. The expanded state is
// opt-in and persisted so the toggle sticks across reloads.
function readPersistedPulseExpanded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(PULSE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function sortBy(
  tenants: TenantSummary[],
  sortId: SortId,
): TenantSummary[] {
  const arr = tenants.slice();
  if (sortId === "atencion") {
    arr.sort((a, b) => {
      const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      return sev !== 0 ? sev : (a.name ?? "").localeCompare(b.name ?? "");
    });
  } else if (sortId === "nombre") {
    arr.sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", "es-UY"),
    );
  } else {
    arr.sort((a, b) => {
      const aT = a.lastActivityAt ? Date.parse(a.lastActivityAt) : 0;
      const bT = b.lastActivityAt ? Date.parse(b.lastActivityAt) : 0;
      return bT - aT;
    });
  }
  return arr;
}

function formatActivity(iso: string | null): { label: string; muted: boolean } {
  if (!iso) return { label: "Sin actividad", muted: true };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { label: "Sin actividad", muted: true };
  }
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (minutes < 1) return { label: "Ahora", muted: false };
  if (minutes < 60) return { label: `Hace ${minutes}m`, muted: false };
  if (hours < 24) return { label: `Hace ${hours}h`, muted: false };
  if (days === 1) return { label: "Ayer", muted: false };
  if (days < 7) return { label: `Hace ${days} días`, muted: false };
  return { label: `Hace ${Math.floor(days / 7)} sem`, muted: true };
}


/**
 * IVA mini-stat cell. Convention shared with the design handoff:
 *   · 0     → "Sin operar", muted
 *   · < 0   → "−X" (credit to the tenant), olive (.v.ok)
 *   · > 0   → "+X" (debt to pay), copper (.v.warn)
 */
// Plan id → user-facing label (matches `userFacingName` in
// apps/backend/src/auth/plans.config.ts). The backend already returns
// the effective plan ('free' | 'pro' | 'ultra'); legacy values are
// normalized server-side, so the fallback should never fire.
function avatarInitials(tenant: TenantSummary): string {
  const name = (tenant.name ?? "").trim();
  if (!name) return "··";
  const parts = name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function AccountantHomePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { switchTenant, isSwitchingTenant, activeTenant } = useTenantSwitcher();
  const queryClient = useQueryClient();
  const { refreshUser, user } = useAuth();

  // URL params win when present; otherwise fall back to the accountant's
  // last-used preference from localStorage. View doesn't go in the URL
  // (it's a personal density choice, not something you'd link to).
  const persistedSort = readPersistedSort();
  const filter = (searchParams.get("filter") as FilterId | null) ?? "all";
  // Régimen is ORTHOGONAL to the status chips — a Literal E client can also
  // need attention — so it is its own parameter rather than another chip.
  // Validated, not cast: a stale or hand-edited ?regimen=foo would otherwise
  // filter every tenant out and leave the Select showing nothing, with no way
  // to tell the empty list came from a bad parameter.
  const regimeParam = searchParams.get("regimen");
  const regimeFilter: TenantRegimeKind | "all" =
    regimeParam != null &&
    (REGIME_FILTER_OPTIONS as string[]).includes(regimeParam)
      ? (regimeParam as TenantRegimeKind)
      : "all";
  const sort =
    (searchParams.get("sort") as SortId | null) ?? persistedSort ?? "atencion";
  const query = searchParams.get("q") ?? "";

  const [view, setView] = useState<ViewId>(() => readPersistedView());
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem("fiscalo:accountant-view", view);
    } catch {
      // Storage unavailable — keep the in-memory choice.
    }
  }, [view]);

  const [pulseExpanded, setPulseExpanded] = useState<boolean>(() =>
    readPersistedPulseExpanded(),
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(PULSE_STORAGE_KEY, pulseExpanded ? "1" : "0");
    } catch {
      // Storage unavailable — keep the in-memory choice.
    }
  }, [pulseExpanded]);

  const setRegimeFilter = (next: TenantRegimeKind | "all"): void => {
    setSearchParams((sp) => {
      if (next === "all") sp.delete("regimen");
      else sp.set("regimen", next);
      return sp;
    });
  };

  const setFilter = (next: FilterId): void => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === "all") sp.delete("filter");
        else sp.set("filter", next);
        return sp;
      },
      { replace: true },
    );
  };
  const setSort = (next: SortId): void => {
    persistSort(next);
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === "atencion") sp.delete("sort");
        else sp.set("sort", next);
        return sp;
      },
      { replace: true },
    );
  };
  const setQuery = (next: string): void => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next.trim() === "") sp.delete("q");
        else sp.set("q", next);
        return sp;
      },
      { replace: true },
    );
  };
  const clearFilters = (): void => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        sp.delete("filter");
        sp.delete("q");
        return sp;
      },
      { replace: true },
    );
  };

  // user.id in the key so a cached response from another accountant
  // can't bleed into this one's render — belt + suspenders alongside
  // the queryClient.clear() in AuthProvider on identity change.
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "tenantsSummary"],
    queryFn: getMyTenantsSummary,
    enabled: Boolean(user?.id),
  });
  const { data: pulse } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "pulse"],
    queryFn: getMyAccountantPulse,
    enabled: Boolean(user?.id),
  });
  const { data: accountantProfile, isLoading: loadingProfile } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "profile"],
    queryFn: getMyAccountantProfile,
    enabled: Boolean(user?.id),
  });
  const filtered = useMemo(() => {
    return sortBy(
      tenants
        .filter((tn) => matchesFilter(tn, filter))
        .filter(
          (tn) =>
            regimeFilter === "all" || describeTenantRegime(tn) === regimeFilter,
        )
        .filter((tn) => matchesSearch(tn, query)),
      sort,
    );
  }, [tenants, filter, regimeFilter, query, sort]);

  // Render in 50-row batches so a studio with hundreds of clients doesn't
  // pay the layout/paint cost upfront. The pulse + filter-chip counts
  // stay accurate because they come from `tenants` (full list) and the
  // pulse endpoint — not from this slice.
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    // Reset to first page whenever the user narrows the visible set.
    setVisibleCount(PAGE_SIZE);
  }, [filter, regimeFilter, query, sort]);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const [pendingDelete, setPendingDelete] = useState<TenantSummary | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [upgradeTenant, setUpgradeTenant] = useState<TenantSummary | null>(
    null,
  );

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      const wasActiveTenant = user?.tenantId === pendingDelete.id;
      await deleteOnboardingTenant(pendingDelete.id);
      toast.success(
        t("accountant.delete.successToast", "Cliente eliminado."),
      );
      setPendingDelete(null);
      if (wasActiveTenant) await refreshUser();
      await queryClient.invalidateQueries({
        queryKey: ["accountant"],
      });
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 409) {
        toast.error(
          t(
            "accountant.delete.alreadyOnboarded",
            "La empresa ya completó onboarding. Pedí la baja desde Ajustes.",
          ),
        );
      } else if (status === 403) {
        toast.error(
          t(
            "accountant.delete.forbidden",
            "Solo el propietario puede eliminar esta empresa.",
          ),
        );
      } else {
        toast.error(
          t(
            "accountant.delete.errorToast",
            "No pudimos eliminar el cliente.",
          ),
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const enter = async (tenant: TenantSummary): Promise<void> => {
    // Onboarding-pending tenants still go straight to the wizard — the
    // accountant needs to finish setup before there's anything to review,
    // and the wizard owns the cert / DGI handshake which require the
    // active session to be on that tenant. Same flow as before.
    //
    // For everything else, navigate to the read-only summary first.
    // Impersonation happens from a CTA on that page — that way the
    // accountant can audit IVA + obligations + recent activity without
    // dropping the session and forcing a cache flush.
    // Only genuinely-incomplete tenants (never finished the wizard) go
    // straight to the wizard. A finished-but-still-test tenant
    // (onboardingCompleted, dgiEnv still 'test') has real data to review and,
    // crucially, must be able to reach the detail page's "Baja de cliente"
    // action — routing it back to the wizard would strand that flow.
    if (!tenant.onboardingCompleted) {
      if (tenant.id !== activeTenant?.id) {
        await switchTenant(tenant.id);
      }
      navigate(`/accountant/client/${tenant.id}/onboarding`);
      return;
    }
    navigate(`/accountant/client/${tenant.id}`);
  };

  if (
    !loadingProfile &&
    accountantProfile?.accountType === "accountant" &&
    accountantProfile.profile == null
  ) {
    return <Navigate to="/onboarding/accountant" replace />;
  }

  if (isLoading) {
    return (
      <div className="ctr-page flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[color:var(--fx-ink-500)]" />
      </div>
    );
  }

  const hasTenants = tenants.length > 0;

  return (
    <div className="ctr-page">
      <PageHeaderBlock onAddClient={() => navigate("/accountant/new-client")} />

      {hasTenants ? (
        <PulseHero
          pulse={pulse}
          totalClientsFallback={tenants.length}
          filter={filter}
          onFilter={setFilter}
          expanded={pulseExpanded}
          onToggleExpanded={() => setPulseExpanded((v) => !v)}
        />
      ) : null}

      {hasTenants ? (
        <Toolbar
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          regimeFilter={regimeFilter}
          onRegimeFilter={setRegimeFilter}
          view={view}
          onView={setView}
        />
      ) : null}

      {!hasTenants ? (
        <EmptyPortfolio
          onAddClient={() => navigate("/accountant/new-client")}
        />
      ) : filtered.length === 0 ? (
        <EmptyFilter
          filter={filter}
          onClear={clearFilters}
        />
      ) : (
        <>
          <div className={cn("ctr-grid", view === "list" && "list")}>
            {/* Column headings, once. In list mode each row used to repeat
                "ACTIVIDAD" and "CFE MES", so those two words appeared as many
                times as the accountant has clients. A list states its columns
                at the top; the rows just fill them. Lives inside the grid so
                it inherits the same width, and mirrors .ccard's column
                template so the headings sit over their own columns. */}
            {view === "list" && visible.length > 0 ? (
              <div className="ctr-list-head" aria-hidden>
                <span />
                <span>{t("accountant.list.colClient", "Cliente")}</span>
                <span>{t("accountant.list.colStatus", "Estado")}</span>
                <span className="ctr-list-head-stats">
                  <span>{t("accountant.card.statActivity", "Actividad")}</span>
                  <span>{t("accountant.card.statCfe", "CFE mes")}</span>
                  <span>{t("accountant.card.statRegime", "Régimen")}</span>
                </span>
              </div>
            ) : null}
            {visible.map((tenant) => (
              <ClientCard
                key={tenant.id}
                tenant={tenant}
                onEnter={() => void enter(tenant)}
                onDelete={
                  !tenant.onboardingCompleted
                    ? () => setPendingDelete(tenant)
                    : undefined
                }
                onUpgrade={
                  tenant.planId === "free"
                    ? () => setUpgradeTenant(tenant)
                    : undefined
                }
                disabled={isSwitchingTenant}
              />
            ))}
          </div>
          {hasMore ? (
            <div className="ctr-load-more">
              <button
                type="button"
                className="ctr-load-more-btn"
                onClick={() =>
                  setVisibleCount((c) => c + PAGE_SIZE)
                }
              >
                {t("accountant.home.loadMore", {
                  count: Math.min(PAGE_SIZE, filtered.length - visibleCount),
                  defaultValue: "Ver {{count}} más",
                })}
              </button>
            </div>
          ) : null}
        </>
      )}

      <ConfirmationDialog
        open={pendingDelete != null}
        onOpenChange={(open) => {
          if (!open && !deleting) setPendingDelete(null);
        }}
        title={t("accountant.delete.title", "Eliminar cliente")}
        description={t("accountant.delete.description", {
          name:
            pendingDelete?.name ??
            pendingDelete?.rut ??
            t("accountant.home.unnamed", "Empresa sin nombre"),
          defaultValue:
            "Vas a eliminar {{name}}. Como todavía no completó onboarding no quedan datos fiscales — la baja es inmediata e irreversible.",
        })}
        confirmText={t("accountant.delete.confirm", "Eliminar")}
        cancelText={t("pages.common.cancel", "Cancelar")}
        variant="destructive"
        isLoading={deleting}
        onConfirm={handleConfirmDelete}
      />

      <GeneratePaymentLinkDialog
        tenant={upgradeTenant}
        onClose={() => setUpgradeTenant(null)}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function PageHeaderBlock({
  onAddClient,
}: {
  onAddClient: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ctr-head">
      <div className="ctr-head-text">
        <span className="eyebrow">
          {t("accountant.home.eyebrow", "Estudio · contadores")}
        </span>
        <h1>
          {t("accountant.home.titleLead", "Tu cartera de")}{" "}
          <span className="ital">
            {t("accountant.home.titleAccent", "clientes.")}
          </span>
        </h1>
        <p className="sub">
          {t(
            "accountant.home.subtitle",
            "Las empresas que gestionás. Ordenadas por lo que pide atención hoy.",
          )}
        </p>
      </div>
      <div className="ctr-head-actions">
        <button
          type="button"
          className="ctr-cta ctr-cta-primary"
          onClick={onAddClient}
        >
          <Plus aria-hidden />
          {t("accountant.home.addClient", "Agregar cliente")}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function PulseHero({
  pulse,
  totalClientsFallback,
  filter,
  onFilter,
  expanded,
  onToggleExpanded,
}: {
  pulse:
    | Awaited<ReturnType<typeof getMyAccountantPulse>>
    | undefined;
  totalClientsFallback: number;
  filter: FilterId;
  onFilter: (next: FilterId) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const totalClients = pulse?.totalClients ?? totalClientsFallback;
  const activeClients = pulse?.activeClients ?? 0;
  const onboardingClients = pulse?.onboardingClients ?? 0;
  const attentionToday = pulse?.attentionToday ?? 0;
  const cfeThisMonth = pulse?.cfeThisMonth ?? 0;
  const cfeGrowth = pulse?.cfeMonthGrowthPct ?? 0;
  // avgHealthScore is null when every tenant is still in onboarding —
  // rendering "0/100" in that case would imply terrible health when the
  // truth is "no active clients to score yet". Treat null as "no data".
  const avgHealth = pulse?.avgHealthScore ?? null;
  const top100 = pulse?.topClientsAt100 ?? { name: null, count: 0 };

  return (
    <section
      className={cn("ctr-pulse", !expanded && "is-collapsed")}
      aria-label="Operational pulse"
    >
      <button
        type="button"
        className="ctr-pulse-toggle"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t("accountant.pulse.collapse", "Ocultar resumen")
            : t("accountant.pulse.expand", "Mostrar resumen")
        }
      >
        {expanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
      </button>

      {!expanded ? (
        <div className="ctr-pulse-summary">
          <span className="ctr-pulse-summary-item">
            <Receipt aria-hidden />
            <strong>{totalClients}</strong>
            <span className="lbl">
              {t("accountant.pulse.portfolioShort", "clientes")}
            </span>
          </span>
          <span className="ctr-pulse-summary-sep" aria-hidden />
          <span
            className={cn(
              "ctr-pulse-summary-item",
              attentionToday > 0 && "attention",
            )}
          >
            <AlertTriangle aria-hidden />
            <strong>{attentionToday}</strong>
            <span className="lbl">
              {t("accountant.pulse.attentionShort", "necesitan atención")}
            </span>
          </span>
          <span className="ctr-pulse-summary-sep" aria-hidden />
          <span className="ctr-pulse-summary-item">
            <Calendar aria-hidden />
            <strong>{cfeThisMonth}</strong>
            <span className="lbl">
              {t("accountant.pulse.cfeShort", "CFE este mes")}
            </span>
          </span>
          <span className="ctr-pulse-summary-sep" aria-hidden />
          <span className="ctr-pulse-summary-item">
            <Heart aria-hidden />
            <strong>{avgHealth ?? "—"}</strong>
            <span className="lbl">
              {t("accountant.pulse.healthShort", "salud promedio")}
            </span>
          </span>
        </div>
      ) : (
      <div className="ctr-pulse-top">
        <div className="ctr-pulse-cell">
          <span className="lbl">
            <Receipt aria-hidden />
            {t("accountant.pulse.portfolio", "Tu cartera")}
          </span>
          <span className="num">{totalClients}</span>
          <span className="desc">
            {t("accountant.pulse.portfolioDesc", {
              active: activeClients,
              onboarding: onboardingClients,
              defaultValue:
                "{{active}} activos · {{onboarding}} en onboarding",
            })}
          </span>
        </div>
        <div className="ctr-pulse-cell">
          <span className="lbl">
            <AlertTriangle aria-hidden />
            {t("accountant.pulse.attention", "Necesitan atención hoy")}
          </span>
          <span className="num attention">{attentionToday}</span>
          <span className="desc">
            {attentionToday === 0
              ? t(
                  "accountant.pulse.attentionDescNone",
                  "Tu cartera está al día.",
                )
              : t(
                  "accountant.pulse.attentionDesc",
                  "Empresas con rechazos, boletos o configuración pendiente.",
                )}
          </span>
        </div>
        <div className="ctr-pulse-cell">
          <span className="lbl">
            <Calendar aria-hidden />
            {t("accountant.pulse.cfe", "CFE emitidos · mes")}
          </span>
          <span className="num">{cfeThisMonth}</span>
          <span className="desc">
            {cfeGrowth === 0
              ? t(
                  "accountant.pulse.cfeDescFlat",
                  "Volumen estable vs. mes anterior",
                )
              : t("accountant.pulse.cfeDesc", {
                  pct: cfeGrowth > 0 ? `+${cfeGrowth}` : `${cfeGrowth}`,
                  defaultValue: "{{pct}}% vs. mes anterior",
                })}
          </span>
        </div>
        <div className="ctr-pulse-cell">
          <span className="lbl">
            <Heart aria-hidden />
            {t("accountant.pulse.health", "Salud promedio")}
          </span>
          <span className="num">
            {avgHealth ?? "—"}
            <span className="unit">/ 100</span>
          </span>
          <span className="desc">
            {avgHealth == null
              ? t(
                  "accountant.pulse.healthDescOnboardingOnly",
                  "Sin clientes activos aún.",
                )
              : top100.count === 0
                ? t(
                    "accountant.pulse.healthDescNone",
                    "Ningún cliente en 100/100 todavía.",
                  )
                : top100.count === 1 && top100.name
                  ? t("accountant.pulse.healthDescSingle", {
                      name: top100.name,
                      defaultValue: "{{name}} está en 100/100.",
                    })
                  : top100.name
                    ? t("accountant.pulse.healthDesc", {
                        name: top100.name,
                        rest: top100.count - 1,
                        defaultValue:
                          "{{name}} y {{rest}} más en 100/100.",
                      })
                    : t("accountant.pulse.healthDescCount", {
                        count: top100.count,
                        defaultValue: "{{count}} cliente(s) en 100/100.",
                      })}
          </span>
        </div>
      </div>
      )}

      <div className="ctr-pulse-chips" role="tablist">
        <span className="label">{t("accountant.pulse.filter", "Filtrar")}</span>
        {FILTERS.map((f) => {
          const count = pulse?.filterCounts[f.id] ?? 0;
          // "Dadas de baja" is an edge state — only surface the chip when the
          // accountant actually has offboarded clients (unless already active).
          if (f.id === "baja" && count === 0 && filter !== "baja") return null;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={cn(
                "chip",
                active && "active",
                f.urgent && "urgent",
              )}
              onClick={() => onFilter(f.id)}
            >
              {t(`accountant.filter.${f.id}`, f.label)}
              <span className="count">{count}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function Toolbar({
  query,
  onQuery,
  sort,
  onSort,
  regimeFilter,
  onRegimeFilter,
  view,
  onView,
}: {
  query: string;
  onQuery: (next: string) => void;
  sort: SortId;
  onSort: (next: SortId) => void;
  regimeFilter: TenantRegimeKind | "all";
  onRegimeFilter: (next: TenantRegimeKind | "all") => void;
  view: ViewId;
  onView: (next: ViewId) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ctr-toolbar">
      <div className="ctr-search">
        <span className="ico" aria-hidden>
          <Search />
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t(
            "accountant.home.searchPlaceholder",
            "Buscar por nombre o RUT",
          )}
          aria-label={t(
            "accountant.home.searchPlaceholder",
            "Buscar por nombre o RUT",
          )}
        />
      </div>
      {/* Régimen is a second, independent axis: a Literal E client can also
          need attention, so it cannot be another chip in a single-select bar.
          Uses the design-system Select, not a native <select> — the portal
          styles every other control through components/ui, and a raw one
          renders the OS menu, which ignores the theme entirely. */}
      <Select
        value={regimeFilter}
        onValueChange={(v) => onRegimeFilter(v as TenantRegimeKind | "all")}
      >
        <SelectTrigger
          className="w-[190px]"
          aria-label={t("accountant.filter.regimeLabel", "Régimen")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            {t("accountant.filter.regimeAll", "Todos los regímenes")}
          </SelectItem>
          {REGIME_FILTER_OPTIONS.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {kind === "unknown"
                ? t("accountant.filter.regimeUnknown", "Sin definir")
                : t(...REGIME_LABEL[kind])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="ctr-sort" role="tablist" aria-label="Sort">
        <button
          type="button"
          role="tab"
          aria-selected={sort === "atencion"}
          className={sort === "atencion" ? "active" : undefined}
          onClick={() => onSort("atencion")}
        >
          <ArrowUpDown aria-hidden />
          {t("accountant.sort.atencion", "Atención")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sort === "nombre"}
          className={sort === "nombre" ? "active" : undefined}
          onClick={() => onSort("nombre")}
        >
          {t("accountant.sort.nombre", "Nombre")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sort === "actividad"}
          className={sort === "actividad" ? "active" : undefined}
          onClick={() => onSort("actividad")}
        >
          {t("accountant.sort.actividad", "Actividad")}
        </button>
      </div>
      <div className="ctr-view-toggle" role="tablist" aria-label="View">
        <button
          type="button"
          role="tab"
          aria-selected={view === "cards"}
          aria-label={t("accountant.home.viewCards", "Tarjetas")}
          className={view === "cards" ? "active" : undefined}
          onClick={() => onView("cards")}
        >
          <LayoutGrid aria-hidden />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          aria-label={t("accountant.home.viewList", "Lista")}
          className={view === "list" ? "active" : undefined}
          onClick={() => onView("list")}
        >
          <ListIcon aria-hidden />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function ClientCard({
  tenant,
  onEnter,
  onDelete,
  onUpgrade,
  disabled,
}: {
  tenant: TenantSummary;
  onEnter: () => void;
  onDelete?: () => void;
  onUpgrade?: () => void;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const activity = formatActivity(tenant.lastActivityAt);
  const initials = avatarInitials(tenant);
  const displayName =
    tenant.name?.trim() ||
    t("accountant.home.unnamed", "Empresa sin nombre");

  const pills: Array<{
    key: string;
    kind: "urgent" | "attention" | "ok" | "muted" | "default" | "danger";
    label: string;
    icon?: React.ReactNode;
  }> = [];
  // Offboarded tenant: show only the "Dada de baja" badge (+ the plan pill
  // below), never the active-state action pills — the client is out of the
  // active cartera.
  const isBaja = tenant.bajaAt != null;
  if (isBaja) {
    pills.push({
      key: "baja",
      kind: "urgent",
      icon: <AlertTriangle aria-hidden />,
      label: t("accountant.card.baja", "Dada de baja"),
    });
  }
  // ONE reason line instead of the pile of pills this replaced. The card
  // could stack onboarding + sin-dgi + boletos + ack + iva + vencimiento +
  // beneficio-fiscal + rejected at once, which is what "I can't tell what
  // needs action" was describing: every reason shown, none prioritised.
  // Selection lives in tenantAction.ts so the ordering is testable; the copy
  // stays here. `+N` keeps the rest discoverable without spending a row on it.
  const action = isBaja ? null : describeTenantAction(tenant);
  if (action) {
    const detail = ACTION_LABEL[action.key](t, tenant, action.count ?? 0);
    pills.push({
      key: `action-${action.key}`,
      kind: action.urgency === "urgent" ? "urgent" : "attention",
      icon: <AlertTriangle aria-hidden />,
      label:
        action.extra > 0
          ? t("accountant.card.actionPlusMore", {
              detail,
              count: action.extra,
              defaultValue: "{{detail}}  +{{count}}",
            })
          : detail,
    });
  }
  // Keyed off "no action" rather than severity === 'ok'. The two agree today,
  // but if a severity signal is ever added without a matching flag, this
  // degrades to "Al día" instead of rendering a card with no pill at all —
  // the blank-card failure the previous per-flag pills were patched to avoid.
  if (!isBaja && !action) {
    // Both conditions. Keying off !action alone was meant to guarantee the
    // card never renders pill-less, but it can claim "Al día" beside a red
    // severity stripe when a severity signal has no matching action key —
    // asserting everything is fine on a client the same card marks urgent.
    // A severity with no action degrades to a neutral pill instead.
    const settled = tenant.severity === "ok";
    pills.push({
      key: "ok",
      kind: settled ? "ok" : "muted",
      icon: settled ? <Check aria-hidden /> : undefined,
      label: settled
        ? t("accountant.card.al-dia", "Al día")
        : t("accountant.card.reviewNeeded", "Revisar"),
    });
  }
  const regime = describeTenantRegime(tenant);
  const cfeCell =
    tenant.invoicesThisMonth > 0
      ? { label: String(tenant.invoicesThisMonth), muted: false }
      : { label: "—", muted: true };
  const roleLabel = t(
    `users.role.${tenant.role}`,
    ROLE_FALLBACKS[tenant.role] ?? tenant.role,
  );

  return (
    <button
      type="button"
      className={cn("ccard", tenant.severity)}
      disabled={disabled}
      onClick={onEnter}
    >
      {onDelete ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={t("accountant.delete.aria", "Eliminar cliente")}
          className="ccard-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }
          }}
        >
          <Trash2 aria-hidden />
        </span>
      ) : null}

      {onUpgrade ? (
        <div
          className="ccard-menu"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="ccard-menu-trigger"
                aria-label={t("accountant.card.menuAria", "Más acciones")}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onUpgrade();
                }}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {t(
                  "accountant.card.migrateToPaid",
                  "Migrar a plan pago",
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}

      {/* Brand-coloured band with the logo on a neutral tile, rather than the
          logo stretched across the band itself. See .ccard-hero. brandColor is
          inline because it is per-tenant data, not a palette token. */}
      <div
        className="ccard-hero"
        // A CUSTOM PROPERTY, not `background` directly. An inline background
        // would beat every stylesheet rule, so the list view could not strip
        // the band — inline style outranking a class is a trap this codebase
        // has already been bitten by. Setting a variable lets CSS decide
        // whether to use it.
        style={
          safeBrandColor(tenant.brandColor)
            ? ({ "--ccard-brand": tenant.brandColor } as React.CSSProperties)
            : undefined
        }
      >
        <span className="ccard-badge">
          {tenant.profileImage ? (
            <img src={tenant.profileImage} alt="" />
          ) : (
            <span className="ccard-badge-initials" aria-hidden>
              {initials}
            </span>
          )}
        </span>
      </div>

      <div className="ccard-top">
        <div className="min-w-0">
          <div className="ccard-name">{displayName}</div>
          <div className="ccard-meta">
            <span className="role">{roleLabel}</span>
            {tenant.rut ? (
              <>
                <span className="sep" aria-hidden />
                <span>{tenant.rut}</span>
              </>
            ) : null}
          </div>
        </div>
        <span className="ccard-chev" aria-hidden>
          <ChevronRight />
        </span>
      </div>

      {pills.length > 0 ? (
        <div className="ccard-pills">
          {pills.map((p) => (
            <span key={p.key} className={cn("pill", p.kind)}>
              {p.icon}
              {p.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="ccard-stats">
        <div className="ccard-stat">
          <span className="k">
            {t("accountant.card.statActivity", "Actividad")}
          </span>
          <span className={cn("v", activity.muted && "muted")}>
            {activity.label}
          </span>
        </div>
        <div className="ccard-stat">
          <span className="k">
            {t("accountant.card.statCfe", "CFE mes")}
          </span>
          <span className={cn("v", cfeCell.muted && "muted")}>
            {cfeCell.label}
          </span>
        </div>
        <div className="ccard-stat">
          <span className="k">
            {t("accountant.card.statRegime", "Régimen")}
          </span>
          <span className={cn("v", regime === "unknown" && "muted")}>
            {t(...REGIME_LABEL[regime])}
          </span>
        </div>
      </div>
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function EmptyPortfolio({
  onAddClient,
}: {
  onAddClient: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ctr-empty">
      <div className="ring" aria-hidden>
        <Receipt />
      </div>
      <h3>
        {t("accountant.home.emptyTitleLead", "Empezá tu")}{" "}
        <span className="ital">
          {t("accountant.home.emptyTitleAccent", "cartera.")}
        </span>
      </h3>
      <p>
        {t(
          "accountant.home.empty",
          "Todavía no tenés clientes. Agregá uno o esperá a que te inviten desde una empresa.",
        )}
      </p>
      <button type="button" className="ctr-cta ctr-cta-primary" onClick={onAddClient}>
        <Plus aria-hidden />
        {t("accountant.home.addClient", "Agregar cliente")}
      </button>
    </div>
  );
}

function EmptyFilter({
  filter,
  onClear,
}: {
  filter: FilterId;
  onClear: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const isPositiveAbsence =
    filter === "attention" || filter === "boletos" || filter === "sin-dgi";
  return (
    <div className="ctr-empty">
      <div className="ring" aria-hidden>
        <Check />
      </div>
      <h3>
        {t("accountant.empty.titleLead", "Ningún cliente con")}{" "}
        <span className="ital">
          {t("accountant.empty.titleAccent", "esta condición.")}
        </span>
      </h3>
      <p>
        {isPositiveAbsence
          ? t(
              "accountant.empty.positive",
              "Tu cartera está al día. Bien hecho.",
            )
          : t(
              "accountant.empty.negative",
              "Probá con otro filtro o limpiá la búsqueda.",
            )}
      </p>
      <button type="button" className="clear" onClick={onClear}>
        {t("accountant.empty.clear", "Limpiar filtros")}
      </button>
    </div>
  );
}
